import * as React from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
    AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';
import JobsList from './JobsList';
import { useAppSelector } from "@store/hooks";

const name = 'CollapsibleList';

const failedIds = [4, 11, 12, 13, 14, 16, 59, 60, 61]
const completedIds = [10, 41, 110,]
const allocatedIds = [3, 9, 27, 36, 37, 38, 42, 43, 48, 56, 62, 66, 97, 98, 100]

const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor:
        theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, .05)'
            : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

interface CollapsibleListProps {
    setOpen: any;
    setJobId: any;
}

const CollapsibleList: React.FC<CollapsibleListProps> = ({ setOpen, setJobId }) => {
    const [expanded, setExpanded] = React.useState<string | false>('completed');
    const [allocated, setAllocated] = React.useState<any[]>([]);
    const [completed, setCompleted] = React.useState<any[]>([]);
    const [failed, setFailed] = React.useState<any[]>([]);

    const bookings = useAppSelector(({ temp }) => temp.Last2);

    React.useEffect(() => {
        const allocated = bookings.filter(
            (b) => allocatedIds.includes(b.statusId!)
        );
        const completed = bookings.filter(
            (b) => (completedIds.includes(b.statusId!)) && b.completion?.inProgress === false
        );
        const failed = bookings.filter(
            (b) => failedIds.includes(b.statusId!)
        );
        setAllocated(allocated);
        setCompleted(completed);
        setFailed(failed);
    }, [bookings]);

    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? panel : false);
        };

    return (
        <Box>
            {completed.length > 0 &&
                <Accordion expanded={expanded === "completed"} onChange={handleChange("completed")}>
                    <AccordionSummary aria-controls="completed-content" id="completed-header">
                        <Typography>{`Completed Bookings (${completed.length})`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <JobsList setJobId={setJobId} setOpen={setOpen} jobs={completed} type="completed" />
                    </AccordionDetails>
                </Accordion>
            }
            {allocated.length > 0 &&
                <Accordion expanded={expanded === "progress"} onChange={handleChange("progress")}>
                    <AccordionSummary aria-controls="progressed-content" id="progressed-header">
                        <Typography>{`Allocated Bookings (${allocated.length})`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <JobsList setJobId={setJobId} setOpen={setOpen} jobs={allocated} type="progress" />
                    </AccordionDetails>
                </Accordion>
            }
            {failed.length > 0 &&
                <Accordion expanded={expanded === "failed"} onChange={handleChange("failed")}>
                    <AccordionSummary aria-controls="failed-content" id="failed-header">
                        <Typography>{`Bookings failed (${failed.length})`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <JobsList setJobId={setJobId} setOpen={setOpen} jobs={failed} type="failed" />
                    </AccordionDetails>
                </Accordion>
            }
        </Box>
    );
}

CollapsibleList.displayName = name;

export default React.memo(CollapsibleList);